import { supabase } from "@/lib/supabaseClient";

export type Role = "user" | "assistant";

export interface ConversationMessage {
  role: Role;
  content: string;
  timestamp: number;
}

export interface Conversation {
  id: string;
  title: string;
  messages: ConversationMessage[];
  languageName: string;
  languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  createdAt: number;
  updatedAt: number;
  conversationKind: "writing" | "speaking";
  continuedFromConversationId: string | null;
  assistantTurnCount: number;
  canContinue: boolean;
}

export interface ContinueConversationResult {
  continued: boolean;
  conversation?: Conversation;
  conversationId?: string;
  sourceConversationId?: string;
  languageName?: string;
  languageLevel?: Conversation["languageLevel"];
  previousTutorMessages?: string[];
}

export class ConversationAPIError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ConversationAPIError";
  }
}

const BASE = "/api/conversations";

async function http<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  // Attach Authorization: Bearer <access_token> if user is signed in
  const authHeaders: Record<string, string> = {};
  try {
    const { data } = await supabase.auth.getSession();
    const access = data?.session?.access_token;
    if (access) {
      authHeaders["Authorization"] = `Bearer ${access}`;
    }
  } catch {}

  const res = await fetch(input, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ConversationAPIError(res.status, text || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function listConversations(q?: string): Promise<Conversation[]> {
  const url = new URL(BASE, typeof window !== "undefined" ? window.location.origin : "");
  if (q) url.searchParams.set("q", q);
  const data = await http<{ conversations: Conversation[] }>(url.toString());
  return data.conversations;
}

export async function createConversation(payload: {
  title?: string;
  languageName: string;
  languageLevel: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
  messages?: ConversationMessage[];
  conversationKind?: "writing" | "speaking";
}): Promise<Conversation> {
  const data = await http<{ conversation: Conversation }>(BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.conversation;
}

export async function continueConversation(payload: {
  sourceSurface: "history" | "settings";
  sourceConversationId?: string;
  languageName?: string;
  languageLevel?: Conversation["languageLevel"];
}): Promise<ContinueConversationResult> {
  return http<ContinueConversationResult>(`${BASE}/continue`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getConversation(id: string): Promise<Conversation> {
  const data = await http<{ conversation: Conversation }>(`${BASE}/${id}`);
  return data.conversation;
}

export async function updateConversationAPI(
  id: string,
  payload: {
    title?: string;
    languageName?: string;
    languageLevel?: "absolute_beginner" | "beginner" | "intermediate" | "advanced";
    messages?: ConversationMessage[];
  }
): Promise<Conversation> {
  const data = await http<{ conversation: Conversation }>(`${BASE}/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return data.conversation;
}

export async function deleteConversationAPI(id: string): Promise<void> {
  await http(`${BASE}/${id}`, { method: "DELETE" });
}

export async function migrateConversations(
  conversations: Conversation[],
  strategy: "merge" | "replace" = "merge"
): Promise<void> {
  await http(`${BASE}/migrate`, {
    method: "POST",
    body: JSON.stringify({ conversations, strategy }),
  });
}

export const STORAGE_KEY = "chicky-conversations";

export function readLocalConversations(): Conversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Conversation[];
  } catch {
    return [];
  }
}

export function writeLocalConversations(convs: Conversation[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
  } catch {}
}
